"use client";
import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  // useDisclosure,
} from "@nextui-org/react";
import { useDictionary } from "../../context/dictionaryProvider";
import { ContactWrapShape, SecondaryBtn, SendBtn } from "../molecules";
import InputTextField from "../atoms/InputTextField";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { staticFormsActions } from "../../actions/staticForms";
import { bitrixLeadAction } from "../../actions/bitrix";
import { zohoLeadAction } from "../../actions/zoho";
import { bitrixSourceIds } from "../../lib/util";
import { trackEnquiryTypeFormSubmit } from "../../lib/tracking";
import ThankuModal from "./ThankuModal";
import Loading from "../../app/[lang]/loading";
import PhoneField from "../atoms/PhoneField";
import { useSearchParams } from "next/navigation";
import { leadFields } from "../../emailTemplates/leadTemplate";
import { leadEmailAction } from "../../actions/email";
import EnquiryTypeToggle from "../atoms/EnquiryTypeToggle";
import { isCorporateEmail } from "../../lib/emailDomain";
import { trackEvent } from "../../lib/engageXClient";
import { useFormTracking } from "../../lib/useFormTracking";

import { trackMetaEvent } from "../../lib/metaTracking";

const phoneRegex = /^\+\d{1,3}\s?\d{4,14}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const formSchema = yup
  .object({
    name: yup.string().required(),
    email: yup
      .string()
      .trim()
      .required("Email is required")
      .email("Please enter a valid email address")
      .matches(emailRegex, "Please enter a valid email address")
      .test(
        "corporate-email",
        "Please enter a valid work email",
        function (value) {
          if (!this.parent?.isCorporate || !value) return true;
          return isCorporateEmail(value);
        }
      ),
    phone: yup
      .string()
      .matches(phoneRegex, "Phone number is not valid")
      .required("Phone number is required"),
    message: yup.string().trim().required(),
    isCorporate: yup.boolean().required(),
  })
  .required();

const EnquireModal = ({
  isOpen = false,
  onOpenChange,
  course,
  sessionDate = "N/A",
  bitrixSourceId = bitrixSourceIds.enquireNow,
  bitrixFields = {},
  showEnquiryTypeToggle = true,
  title,
  section = "modal",
  formName = "enquire",
  // Opens with "Corporate" already chosen (the visitor can still switch).
  defaultCorporate = false,
}) => {

  const searchParams = useSearchParams();
  const dict = useDictionary();
  const [response, setResponse] = useState("");
  const [thankyou, setThankyou] = useState(false);
  const [countryName, setCountryName] = useState("");
  const ft = useFormTracking({ formName, open: isOpen, trigger: section });

  // Opening the form is a separate, weaker signal than submitting one. The
  // retired GTM tags fired Lead here, so anyone who opened a form and then
  // submitted it was counted twice.
  useEffect(() => {
    if (!isOpen) return;
    trackMetaEvent({
      eventName: "FormOpened",
      customData: {
        content_name: course?.course_name || course?.name || "Enquire",
        content_category: "enquire_modal",
      },
    });
  }, [isOpen]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      isCorporate: defaultCorporate,
    },
  });

  const isCorporate = watch("isCorporate", false);

  const onSubmit = async (data) => {
    const dataWithCountry = {
      ...data,
      country_name: countryName || ""
    };
    try {
      if (typeof trackEnquiryTypeFormSubmit === "function") {
        trackEnquiryTypeFormSubmit("enquire_modal", data?.isCorporate);
      }
      const res = await staticFormsActions("EnquireNow", data);

      if (!res?.status) {
        setResponse("Unable to submit your enquiry. Please try again.");
        return;
      }

      setThankyou(true);
      reset({
        isCorporate: defaultCorporate,
      });

      const utm = {
        utm_source: localStorage.getItem("utm_source") || "",
        utm_medium: localStorage.getItem("utm_medium") || "",
        utm_campaign: localStorage.getItem("utm_campaign") || "",
        utm_term: localStorage.getItem("utm_term") || "",
        utm_content: localStorage.getItem("utm_content") || "",
        click_id: localStorage.getItem("click_id") || "",
      };
      trackEvent("enquiry_submitted", {
        form: "enquire_modal",
        course: course?.course_name || course?.name,
        section,
        ...ft.submitted({ isCorporate: data?.isCorporate, country: countryName }),
      });
      trackMetaEvent({
        eventName: "Lead",
        email: data?.email,
        phone: data?.phone,
        customData: {
          content_name: course?.course_name || course?.name || course?.slug,
          content_category: "enquire_modal",
          content_ids: course?.slug ? [course.slug] : undefined,
        },
      });

      void Promise.allSettled([
        zohoLeadAction(
          { ...dataWithCountry, ...utm, course },
          searchParams,
          bitrixSourceId,
          title || "Enquire Now"
        ),
        bitrixLeadAction(
          { ...data, course, bitrixFields },
          searchParams,
          bitrixSourceId
        ),
        leadEmailAction("Enquiry", leadFields(data)),
      ]).then((results) => {
        results.forEach((result) => {
          if (result.status === "rejected") {
            console.error("Enquire Now integration failed:", result.reason);
          }
        });
      });
    } catch (error) {
      console.log(`Error form submission: EnquireNow`, error?.message);
    }
  };



  return (
    <>
      {isOpen && (
        <Modal
          backdrop="blur"
          isOpen={isOpen}
          size="xl"
          classNames={{
            backdrop: "blur-md",
          }}
          scrollBehavior="outside"
          onOpenChange={onOpenChange}
          onClose={reset}
        >
          <ModalContent>
            {(onClose) => (
              <>
                {!isSubmitting ? (
                  <ModalBody>
                    {!thankyou ? (
                      <form
                        ref={ft.ref}
                        onSubmit={handleSubmit(onSubmit)}
                        className="relative bg-[#fff] my-12 md:mx-10"
                      >
                        <div className="px-12 pb-28 pt-10 rounded-2xl border border-black">
                          <h3 className="text-2xl text-[#303030] mb-10 font-bold capitalize">
                            {title || dict?.enquireNow}
                          </h3>
                          <input type="hidden" {...register("isCorporate")} />
                          {showEnquiryTypeToggle && (
                            <EnquiryTypeToggle
                              isCorporate={isCorporate}
                              onChange={(value) =>
                                setValue("isCorporate", value, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                })
                              }
                            />
                          )}
                          <InputTextField
                            label={dict?.form.fieldNames.name}
                            type={"text"}
                            isInvalid={!!errors?.name}
                            errorMessage={errors?.name?.message}
                            {...register("name")}
                          />
                          <InputTextField
                            label={
                              isCorporate
                                ? dict?.form?.fieldNames?.work_email || "Work Email"
                                : dict?.form.fieldNames.email
                            }
                            type={"email"}
                            isInvalid={!!errors?.email}
                            errorMessage={errors?.email?.message}
                            {...register("email")}
                          />
                          <PhoneField
                              label={dict?.form.fieldNames.phone}
                              name="phone"
                              value={getValues("phone")}
                              onChange={(value, metadata) => {
                                setValue("phone", value, { shouldDirty: true, shouldValidate: isDirty });
                                //  Capture country name
                                if (metadata?.country?.name) {
                                  setCountryName(metadata.country.name);
                                }
                              }}
                              isInvalid={!!errors?.phone}
                              errorMessage={errors?.phone?.message}
                            />
                          <InputTextField
                            label={dict?.form.fieldNames.message}
                            type={"text"}
                            isInvalid={!!errors?.message}
                            errorMessage={errors?.message?.message}
                            {...register("message")}
                          />
                          {response}
                        </div>
                        <SendBtn
                          type="submit"
                          className="!p-4"
                          id="enquire-now"
                        >
                          {dict?.sendNow}
                        </SendBtn>
                        <ContactWrapShape />
                      </form>
                    ) : (
                      <ThankuModal
                      course={course}
                        message={
                          "Thank you for submitting your query! Our team will get back to you soon!"
                        }
                        btnText={"Close"}
                        handleClick={() => {
                          onOpenChange();
                          setThankyou((prev) => !prev);
                        }}
                      />
                    )}
                  </ModalBody>
                ) : (
                  <Loading />
                )}
              </>
            )}
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default EnquireModal;
